import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { LuGitBranch } from 'react-icons/lu';

import { fetchAdrDriftCheckSetting, updateAdrDriftCheckSetting } from '../../shared/api/user-settings';

// MF-59/MF-60 — the weekly ADR drift-check job clones and runs an LLM investigation over a user's
// repo in the background; opt-in, default false, until this toggle is turned on nothing dispatches
// for that user at all. Per-user (not per-project) — matches the BE contract.
const AdrDriftCheckSetting: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAdrDriftCheckSetting()
      .then((s) => !cancelled && setEnabled(s.enabled))
      .catch(() => !cancelled && setLoadError('Failed to load this setting.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setIsSaving(true);
    setError(null);
    try {
      const saved = await updateAdrDriftCheckSetting(next);
      setEnabled(saved.enabled);
      toast.success(saved.enabled ? 'Weekly ADR drift-check enabled.' : 'Weekly ADR drift-check disabled.');
    } catch {
      setEnabled(!next);
      setError('Failed to update this setting.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-4">
          <div className="skeleton h-12 w-full" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-error shadow-card">
        <span>{loadError}</span>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-card">
      <div className="card-body p-4 gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="text-lg shrink-0 opacity-60">
              <LuGitBranch />
            </span>
            <div>
              <div className="font-semibold">Architecture decision drift check</div>
              <div className="text-sm opacity-70 max-w-lg">
                Periodically re-check my accepted architecture decisions against the actual code — clones and inspects
                the repo in the background, weekly.
              </div>
            </div>
          </div>
          <input
            type="checkbox"
            className="toggle toggle-primary shrink-0"
            checked={enabled}
            onChange={handleToggle}
            disabled={isSaving}
            title="Enable weekly ADR drift check"
          />
        </div>

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdrDriftCheckSetting;
